import { v4 as uuidv4 } from 'uuid';

import { getCookie } from 'modules/common/utils';
import { onPreferences } from 'utils/analytics/datagrailConsent';

// Kept out of `./utils` because many tests mock that module with explicit factories, and anything
// app-wide that calls into it (like `AuthProvider`) would hit a member those factories leave out.

const isUsableAnonymousId = (value: unknown): value is string =>
    typeof value === 'string' && value !== '' && value !== 'null';

const readStoredAnonymousId = (): string | undefined => {
    try {
        const stored = localStorage.getItem('cltv_anonymous_id');

        return isUsableAnonymousId(stored) ? stored : undefined;
    } catch {
        return undefined;
    }
};

const STUB_POLL_INTERVAL_MS = 250;
const STUB_POLL_ATTEMPTS = 20;

const waitForTrackingStub = (onFound: () => void, attempt = 0) => {
    if (window.collectiveTracking) {
        onFound();
        return;
    }
    if (attempt < STUB_POLL_ATTEMPTS) {
        setTimeout(() => waitForTrackingStub(onFound, attempt + 1), STUB_POLL_INTERVAL_MS);
    }
};

export const onTrackingScriptReady = (callback: () => void) => {
    if (window.collectiveTracking) {
        window.collectiveTracking('onReady', callback);
        return;
    }

    // DataGrail holds the `index.html` stub back until consent, which can be granted mid-session.
    // Its callback and the stub being released can land in either order, hence the short wait.
    let registered = false;
    const stopListening = onPreferences(() => {
        waitForTrackingStub(() => {
            if (registered) {
                return;
            }
            registered = true;
            stopListening();
            window.collectiveTracking?.('onReady', callback);
        });
    });
};

export const getAnonymousId = (): string => {
    // Until the tracking script loads, the `index.html` stub queues the call and returns the
    // queue length, so anything but a string is not an id. Statsig buckets on this value at boot.
    const fromScript = window.collectiveTracking?.('getAnonymousId');
    // Same precedence as the tracking script, which adopts a `cltvaid` handed over in the URL first.
    // Booting Statsig on anything else buckets the visitor on one id and swaps to the tracker's later.
    const fromUrl = new URLSearchParams(window.location.search).get('cltvaid');

    const anonymousId =
        [fromScript, fromUrl, getCookie('cltv_anonymous_id')].find(isUsableAnonymousId) ??
        readStoredAnonymousId() ??
        uuidv4();

    // Best effort: storage can throw when site data is blocked, and this runs while Statsig boots.
    try {
        localStorage.setItem('cltv_anonymous_id', anonymousId);
    } catch {
        // The id is still returned, it just won't survive a reload.
    }

    return anonymousId;
};
